import { useState, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { message } from "antd";
import { fetchCompanyAllBrandsAction } from "../../../slices/internalToolSlice";
import { fetchCompaniesWithBrandsData } from "../../../slices/competitorsSlice";

// UI weight key -> backend pet_formula weight key. Shared by every flow that
// writes a pet_formula (Create Workspace, Add Market).
export const KPI_TO_API_KEY_MAP = {
  market_penetration_client: "market_penetration",
  market_penetration_listed_drinks: "market_penetration_branded",
  avg_core_sku_presence_client_dsf: "core_availability",
  combo_presence: "combo_presence",
  fixed_combo_presence: "fixed_combo_presence",
  option_combo_presence: "option_combo_presence",
  name_accuracy: "name_accuracy",
  avg_image_presence: "image_presence",
};

/**
 * Shared per-market configuration state + helpers used by both the Create
 * Workspace wizard and the Add Market wizard. Owns the `regionId|market`-keyed
 * weights and assignments (company / core-brand groups / competitors) plus the
 * brand/competitor TreeSelect data and the UI→API payload transform — i.e.
 * everything Step2 (weights) and Step3 (assignments) consume.
 *
 * The caller owns Step1 (how `customRegions` and `clientId` are produced) and
 * the submit/validation flow; this hook owns the market-config middle.
 *
 * @param {object} args
 * @param {string} args.clientId - The client used for brand/competitor lookups.
 * @param {object} args.customRegions - `{ [regionId]: { name, markets: [] } }`.
 */
export const useMarketConfig = ({ clientId, customRegions }) => {
  const dispatch = useDispatch();
  const {
    skuCompanies,
    skuCompaniesLoading,
    companyBrands,
    companyBrandsLoading,
  } = useSelector((state) => state.internalTool);
  const {
    companiesWithBrands,
    loadingByCountry: companiesWithBrandsLoadingByCountry,
  } = useSelector((state) => state.competitors);

  const [marketWeights, setMarketWeights] = useState({});
  const [savedDefaults, setSavedDefaults] = useState([]);
  const [selectedDefault, setSelectedDefault] = useState(null);
  const [marketAssignments, setMarketAssignments] = useState({});
  const [coreBrandDraftSelections, setCoreBrandDraftSelections] = useState({});
  const [coreBrandDraftName, setCoreBrandDraftName] = useState({});

  // Monotonic counter for unique group / template ids — deterministic (no
  // Date.now/Math.random) so the ids are stable and testable.
  const idCounter = useRef(0);
  const nextId = useCallback(() => {
    idCounter.current += 1;
    return String(idCounter.current);
  }, []);

  const getPairs = useCallback(() => {
    const arr = [];
    Object.entries(customRegions || {}).forEach(([rid, r]) =>
      (r.markets || []).forEach((m) =>
        arr.push({ regionId: rid, regionName: r.name, market: m })
      )
    );
    return arr;
  }, [customRegions]);

  const calcTotal = useCallback(
    (ck) =>
      Object.values(marketWeights[ck] || {}).reduce(
        (s, v) => s + (Number(v) || 0),
        0
      ),
    [marketWeights]
  );

  const updateWeight = useCallback((ck, k, v) => {
    setMarketWeights((p) => ({
      ...p,
      [ck]: { ...(p[ck] || {}), [k]: v },
    }));
  }, []);

  const saveDefaultFor = useCallback(
    (ck) => {
      if (Math.abs(calcTotal(ck) - 100) > 0.1) {
        message.error("Weights must total 100%");
        return;
      }
      const def = {
        id: nextId(),
        name: "Template " + (savedDefaults.length + 1),
        weights: { ...(marketWeights[ck] || {}) },
      };
      setSavedDefaults((p) => [...p, def]);
      setSelectedDefault(def.id);
    },
    [calcTotal, savedDefaults, marketWeights, nextId]
  );

  const applyDefaultTo = useCallback(
    (ck) => {
      if (!selectedDefault) return;
      const def = savedDefaults.find((d) => d.id === selectedDefault);
      if (!def) return;
      setMarketWeights((p) => ({ ...p, [ck]: { ...(def.weights || {}) } }));
    },
    [selectedDefault, savedDefaults]
  );

  const companies = useMemo(() => {
    if (
      skuCompanies &&
      typeof skuCompanies === "object" &&
      Object.keys(skuCompanies).length > 0
    ) {
      const allCompanies = new Set();
      Object.values(skuCompanies).forEach((countryCompanies) => {
        if (Array.isArray(countryCompanies)) {
          countryCompanies.forEach((company) => allCompanies.add(company));
        }
      });
      return Array.from(allCompanies);
    }
    return [];
  }, [skuCompanies]);

  const getBrandsTreeData = useCallback(
    (company) => {
      if (!company || !companyBrands[company]) return [];
      const brandsData = companyBrands[company];
      if (!brandsData || !Array.isArray(brandsData.brands)) return [];
      return brandsData.brands.map((brand) => ({
        title: brand.brand,
        value: `brand_${brand.brand}`,
        key: `brand_${brand.brand}`,
        children: brand.subbrands
          ? brand.subbrands.map((subbrand) => ({
              title: subbrand.subbrand,
              value: `brand_${brand.brand}__subbrand_${subbrand.subbrand}`,
              key: `brand_${brand.brand}__subbrand_${subbrand.subbrand}`,
            }))
          : [],
      }));
    },
    [companyBrands]
  );

  const getCompetitorsOptions = useCallback(
    (ck) => {
      const country = ck.split("|")[1];
      const countryData = companiesWithBrands?.[country];
      const selectedCompany = marketAssignments[ck]?.company;
      if (!countryData?.companies || !Array.isArray(countryData.companies)) {
        return [];
      }
      return countryData.companies
        .filter((companyData) => companyData.company !== selectedCompany)
        .map((companyData) => ({
          title: companyData.company,
          value: companyData.company,
          key: companyData.company,
          children:
            companyData.brands?.map((brand) => ({
              title: brand.brand,
              value: `${companyData.company}|${brand.brand}`,
              key: `${companyData.company}|${brand.brand}`,
              companyName: companyData.company,
              brandName: brand.brand,
            })) || [],
        }));
    },
    [companiesWithBrands, marketAssignments]
  );

  const getCompetitorsLoading = useCallback(
    (ck) => {
      const country = ck.split("|")[1];
      const company = marketAssignments[ck]?.company;
      if (!company) return false;
      const isLoading = !!companiesWithBrandsLoadingByCountry?.[country];
      const hasData = companiesWithBrands?.[country] != null;
      return isLoading || !hasData;
    },
    [companiesWithBrandsLoadingByCountry, companiesWithBrands, marketAssignments]
  );

  const addGroup = useCallback(
    (ck) => {
      const sel = coreBrandDraftSelections[ck] || [];
      const name = (coreBrandDraftName[ck] || "").trim();
      const currentAssignment = marketAssignments[ck] || {
        coreBrandGroups: [],
        company: null,
        competitors: [],
      };
      if (!currentAssignment.company) {
        message.warning("Please select a company first");
        return;
      }
      if (!sel.length) {
        message.warning("Select at least one brand/subbrand first");
        return;
      }
      if (!name) {
        message.warning("Enter core brand name");
        return;
      }
      setMarketAssignments((p) => {
        const cur = p[ck] || {
          coreBrandGroups: [],
          company: null,
          competitors: [],
        };
        if (cur.coreBrandGroups.length >= 8) {
          message.warning("Maximum 8 core brands");
          return p;
        }
        const group = {
          id: nextId(),
          name,
          items: sel.map((s) => {
            if (s.startsWith("brand_") && s.includes("__subbrand_")) {
              const parts = s.split("__subbrand_");
              const brandName = parts[0].replace("brand_", "");
              const subbrandName = parts[1];
              return {
                type: "subbrand",
                brand: brandName,
                subbrand: subbrandName,
              };
            } else if (s.startsWith("brand_")) {
              const brandName = s.replace("brand_", "");
              return { type: "brand", brand: brandName };
            }
            console.warn("Unexpected selection format:", s);
            return { type: "unknown", value: s };
          }),
        };
        return {
          ...p,
          [ck]: { ...cur, coreBrandGroups: [...cur.coreBrandGroups, group] },
        };
      });
      setCoreBrandDraftSelections((p) => ({ ...p, [ck]: [] }));
      setCoreBrandDraftName((p) => ({ ...p, [ck]: "" }));
    },
    [coreBrandDraftSelections, coreBrandDraftName, marketAssignments, nextId]
  );

  const removeGroup = useCallback(
    (ck, id) =>
      setMarketAssignments((p) => {
        const cur = p[ck];
        if (!cur) return p;
        return {
          ...p,
          [ck]: {
            ...cur,
            coreBrandGroups: cur.coreBrandGroups.filter((g) => g.id !== id),
          },
        };
      }),
    []
  );

  const setCompany = useCallback(
    (ck, company) => {
      setMarketAssignments((p) => {
        const cur = p[ck] || {
          coreBrandGroups: [],
          company: null,
          competitors: [],
        };
        return { ...p, [ck]: { ...cur, company } };
      });
      if (company && clientId) {
        const market = ck.split("|")[1];
        if (market) {
          dispatch(
            fetchCompanyAllBrandsAction({
              id_client: clientId,
              country: market,
              company,
            })
          );
          dispatch(
            fetchCompaniesWithBrandsData({ id_client: clientId, country: market })
          ).catch((error) => {
            console.error(`🏆 fetchCompaniesWithBrandsData error:`, error);
          });
        }
      }
    },
    [dispatch, clientId]
  );

  const setCompetitors = useCallback(
    (ck, list) =>
      setMarketAssignments((p) => {
        const cur = p[ck] || {
          coreBrandGroups: [],
          company: null,
          competitors: [],
        };
        return { ...p, [ck]: { ...cur, competitors: list } };
      }),
    []
  );

  const applyToAllMarkets = useCallback(
    (sourceCk) => {
      const sourceAssignment = marketAssignments[sourceCk];
      if (
        !sourceAssignment?.company ||
        !sourceAssignment?.coreBrandGroups?.length
      ) {
        message.warning(
          "Complete company and core brands before applying to all"
        );
        return;
      }

      const pairs = getPairs();
      const targetCks = pairs
        .map(({ regionId, market }) => `${regionId}|${market}`)
        .filter((ck) => ck !== sourceCk);

      if (targetCks.length === 0) return;

      setMarketAssignments((prev) => {
        const updated = { ...prev };
        targetCks.forEach((targetCk) => {
          updated[targetCk] = {
            company: sourceAssignment.company,
            coreBrandGroups: sourceAssignment.coreBrandGroups.map((g) => ({
              ...g,
              id: nextId(),
              items: [...g.items],
            })),
            competitors: [...(sourceAssignment.competitors || [])],
          };
        });
        return updated;
      });

      targetCks.forEach((targetCk) => {
        const targetMarket = targetCk.split("|")[1];
        if (targetMarket && clientId) {
          dispatch(
            fetchCompanyAllBrandsAction({
              id_client: clientId,
              country: targetMarket,
              company: sourceAssignment.company,
            })
          );
          dispatch(
            fetchCompaniesWithBrandsData({
              id_client: clientId,
              country: targetMarket,
            })
          );
        }
      });

      const sourceMarket = sourceCk.split("|")[1].toUpperCase();
      message.success(
        `${sourceMarket} settings applied to ${targetCks.length} other market${
          targetCks.length > 1 ? "s" : ""
        }`
      );
    },
    [marketAssignments, getPairs, clientId, dispatch, nextId]
  );

  // Transform one market's staged assignment + weights into the backend
  // per-country shape ({ weights, core_brands, companies, competitors }). Both
  // wizards build their payload from this — Create Workspace per market,
  // Add Market for the single new market.
  const buildMarketConfig = useCallback(
    (ck) => {
      const assignment = marketAssignments[ck] || {};
      const userWeights = marketWeights[ck] || {};

      const weights = Object.entries(KPI_TO_API_KEY_MAP).reduce(
        (acc, [uiKey, apiKey]) => {
          const rawValue = Number(userWeights[uiKey] ?? 0);
          acc[apiKey] = Number.isFinite(rawValue)
            ? parseFloat((rawValue / 100).toFixed(4))
            : 0;
          return acc;
        },
        {}
      );

      const core_brands = (assignment.coreBrandGroups || []).map((group) => ({
        name: group.name,
        content: group.items
          .map((item) => {
            if (item.type === "brand") return { brand: item.brand, subbrand: null };
            if (item.type === "subbrand")
              return { brand: item.brand, subbrand: item.subbrand };
            return null;
          })
          .filter(Boolean),
      }));

      const companiesList = assignment.company ? [assignment.company] : [];

      const competitors = (assignment.competitors || []).map((comp) =>
        comp.brand
          ? { company: comp.competitor, brand: comp.brand }
          : { company: comp.competitor }
      );

      return { weights, core_brands, companies: companiesList, competitors };
    },
    [marketAssignments, marketWeights]
  );

  // Clear all per-market config (used when the client/workspace changes or the
  // modal closes).
  const resetMarketConfig = useCallback(() => {
    setMarketWeights({});
    setSavedDefaults([]);
    setSelectedDefault(null);
    setMarketAssignments({});
    setCoreBrandDraftSelections({});
    setCoreBrandDraftName({});
  }, []);

  return {
    // state
    marketWeights,
    savedDefaults,
    selectedDefault,
    marketAssignments,
    coreBrandDraftSelections,
    coreBrandDraftName,
    // setters (used by steps + reset paths)
    setMarketWeights,
    setSelectedDefault,
    setMarketAssignments,
    setCoreBrandDraftSelections,
    setCoreBrandDraftName,
    // derived data
    companies,
    skuCompaniesLoading,
    companyBrands,
    companyBrandsLoading,
    companiesWithBrands,
    getBrandsTreeData,
    getCompetitorsOptions,
    getCompetitorsLoading,
    // helpers
    getPairs,
    calcTotal,
    updateWeight,
    saveDefaultFor,
    applyDefaultTo,
    addGroup,
    removeGroup,
    applyToAllMarkets,
    setCompany,
    setCompetitors,
    buildMarketConfig,
    resetMarketConfig,
  };
};
